import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { SensorStatusIcon } from './SensorStatusIcon';
import { Link } from 'react-router-dom';
import { getGuests, interpolateData, unlinkSensor } from '../actions/fieldsActions';
import { postNematode, postSolarization, putNematode, putSolarization } from '../actions/modelActions';
import { getColor, getColorFooter, getColorCard, getColorCardFooter, getSolarizationBarPercent, getSolarizationTierKey, getPathogenSolarizationBarPercent, getPathogenLatinLabel, PATHOGEN_SOLARIZATION_ORDER, isPathogenSolarizationLicensed } from '../utils/fieldUtils';
import { availableLicense, warningLicense, formatDateLicence, expirationDays } from '../utils/functions';
import well_signal from '../assets/well_signal_black.svg';
import half_signal from '../assets/half_signal_black.svg';
import low_signal from '../assets/low_signal_black.svg';
import not_signal from '../assets/not_signal_black.svg';
import not_battery from '../assets/not_battery_black.svg';
import low_battery from '../assets/low_battery_black.svg';
import half_battery from '../assets/half_battery_black.svg';
import well_battery from '../assets/well_battery_black.svg';
import not_signal_battery from '../assets/not_signal_battery_black.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faShareNodes, faUserPlus, faUserXmark, faCheck, faCircleInfo, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { ModalShared } from './ModalShared';
import { ModalRejectShared } from './ModalRejectShared';
import { ModalDeleteField } from './ModalDeleteField';
import { ModalLicenseExpiration } from './ModalLicenseExpiration';
import { ModalMoreInfo } from './ModalMoreInfo';
import { Menu } from 'primereact/menu';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { locale, addLocale } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';
import FieldMap from './Map';
import { FieldChartTimelineModal } from './FieldChartTimelineModal';

const FIELD_MAP_DIALOG_HEIGHT_PX = 400;

/** Semáforo solarización: nulo/bajo/moderado rojo; buena naranja; excelente/súper verde. */
const getSolarizationTierBarColor = (tierKey) => {
    if (tierKey === 'null' || tierKey === 'low' || tierKey === 'moderate') return getColor('red');
    if (tierKey === 'good') return getColor('orange');
    if (tierKey === 'excellent' || tierKey === 'super') return getColor('green');
    return getColor('orange');
};

/** Misma rejilla que solarización: nombre | barra dinámica | estado */
const NEMATODE_ROW_GRID = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 21%) minmax(40px, 55%) minmax(0, 1fr)',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    marginBottom: '1rem'
};
/** Rejilla nombre | barra | estado por anchura (saltos): ≥ `BAR_GRID_DEFAULT_MIN_WIDTH` = layout por defecto; entre ese valor y `BAR_GRID_TIGHT_MAX_WIDTH` = intermedio; &lt; tight = más estrecho. */
const BAR_GRID_TIGHT_MAX_WIDTH = 428;
const BAR_GRID_DEFAULT_MIN_WIDTH = 500;

const BAR_GRID_TIGHT_NAME_COL_PCT = 22;
const BAR_GRID_TIGHT_BAR_MAX_PCT = 45;
const BAR_GRID_TIGHT_BAR_MIN_PX = 28;

const BAR_GRID_INTERMEDIATE_NAME_COL_PCT = 22;
const BAR_GRID_INTERMEDIATE_BAR_MAX_PCT = 49;
const BAR_GRID_INTERMEDIATE_BAR_MIN_PX = 34;
const NEMATODE_BAR_TRACK = {
    position: 'relative',
    width: '100%',
    minWidth: 0,
    backgroundColor: '#DEE2E6',
    borderRadius: '50px',
    overflow: 'hidden',
    boxSizing: 'border-box'
};
const NEMATODE_BAR_HEIGHT_PX = 16;
const NEMATODE_PCT_IN_BAR = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '11px',
    fontWeight: '700',
    color: '#212529',
    pointerEvents: 'none',
    zIndex: 1,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textShadow: '0 0 2px #fff, 0 0 4px #fff, 0 1px 1px rgba(255,255,255,0.9)',
    visibility: 'hidden'
};

/**
 * Estado API `blue` («esperar» tras ratio muy alto): en tarjeta se muestra 0 % y color verde como el otro «esperar».
 */
const getNematodeBarUi = (apiColor, pctRaw) => {
    if (apiColor === 'blue') {
        return { barValue: 0, pctLabel: '0', barColor: getColor('green') };
    }
    const barValue = Math.max(0, Math.min(100, Number(pctRaw) || 0));
    return { barValue, pctLabel: String(pctRaw), barColor: getColor(apiColor) };
};

/**
 * Menú popup de PrimeReact: al abrir enfoca el `<ul role="menu">` y marca el primer ítem.
 * Tras la transición se quita el foco del overlay (incl. ítems planos como solo «Desvincular»),
 * para no mostrar el primer elemento como activo.
 */
const MENU_POPUP_TRANSITION_NO_INITIAL_FOCUS = {
    onEntered: (node) => {
        const clearMenuFocus = () => {
            if (!node?.querySelector) return;
            const list = node.querySelector('[role="menu"]');
            list?.blur();
            const actionLink = node.querySelector('a[data-pc-section="action"]');
            actionLink?.blur();
            const ae = document.activeElement;
            if (ae && node.contains(ae)) {
                ae.blur();
            }
        };
        clearMenuFocus();
        requestAnimationFrame(clearMenuFocus);
        setTimeout(clearMenuFocus, 0);
    }
};

/**
 * Primera letra mayúscula y el resto minúsculas (frase); no capitaliza cada palabra como CSS `capitalize`.
 */
const sentenceCaseUi = (value) => {
    if (value == null || value === '') return value;
    const s = String(value).trim();
    if (!s) return value;
    return s.charAt(0).toLocaleUpperCase() + s.slice(1).toLocaleLowerCase();
};

export const FieldCard = ({ fieldId, fieldName, sensorNumber, activeSensor, signalSensor, batterySensor, cropId, model, ownerEmail, userEmailLogin, userId, license_info, isFields, fieldsPerRow, setTrigger, fieldData = null }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isSharedUsersModalOpen, setIsSharedUsersModalOpen] = useState(false);
    const [isRejectSharedModalOpen, setIsRejectSharedModalOpen] = useState(false);
    const [sharedUsers, setSharedUsers] = useState([]);
    const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isMoreInfoModalOpen, setIsMoreInfoModalOpen] = useState(false);
    const [info_text, setInfoText] = useState('');
    const [isUnlinkSensorModalOpen, setIsUnlinkSensorModalOpen] = useState(false);
    const [showStartModelDialog, setShowStartModelDialog] = useState(false);
    const [showStopNematodeDialog, setShowStopNematodeDialog] = useState(false);
    const [showStopSolarizationDialog, setShowStopSolarizationDialog] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsErrorMessage, setOptionsErrorMessage] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [diffDays, setDiffDays] = useState(0);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isWrapped, setIsWrapped] = useState(false);
    const [isMapDialogVisible, setIsMapDialogVisible] = useState(false);
    const [mapAddress, setMapAddress] = useState('');
    const [isChartTimelineModalOpen, setIsChartTimelineModalOpen] = useState(false);
    const [chartModalMode, setChartModalMode] = useState('temperature');
    const [chartModalPathogenCode, setChartModalPathogenCode] = useState(null);
    const optionsMenuRef = useRef(null);
    const labelRef = useRef(null);
    const linkRef = useRef(null);
    let haveSensor = true;
    let isAvailableLicense = null;
    let isWarningLicense = null;
    let daysExpiredLicense = null;

    if (sensorNumber === 'no_sensor'){
        sensorNumber = t('field_card.no_sensor');
        haveSensor = false;
    }

    if (license_info !== null) {
        isAvailableLicense = availableLicense(license_info.expiration_license_date);
        isWarningLicense = warningLicense(license_info.expiration_license_date);
        daysExpiredLicense = expirationDays(license_info.expiration_license_date);
    }

    let mapLat = 0;
    let mapLon = 0;
    if (fieldData?.area?.coordinates && Array.isArray(fieldData.area.coordinates) && fieldData.area.coordinates.length === 2) {
        mapLon = fieldData.area.coordinates[0];
        mapLat = fieldData.area.coordinates[1];
    }
    const hasMapLocation = Boolean(mapLat && mapLon);

    const isAnyModalOpen =
        isLicenseModalOpen ||
        isMoreInfoModalOpen ||
        isSharedUsersModalOpen ||
        isRejectSharedModalOpen ||
        isDeleteFieldModalOpen ||
        isChartTimelineModalOpen ||
        isMapDialogVisible ||
        isUnlinkSensorModalOpen ||
        showStartModelDialog ||
        showStopNematodeDialog ||
        showStopSolarizationDialog;

    const menuModelType = model[0] === 'Nematode active' ? 'N' : model[0] === 'Solarization active' ? 'S' : 'No model';
    const isOwner = userEmailLogin === ownerEmail;
    const showOwnerOptionsMenu = Boolean(isFields && isOwner && fieldData);
    /** Invitado: solo «desvincular» del campo compartido (mismo flujo que el antiguo enlace del pie). */
    const showGuestOptionsMenu = Boolean(isFields && !isOwner && fieldData);
    const showOptionsMenuHeader = showOwnerOptionsMenu || showGuestOptionsMenu;

    const openLicenseModal = (e) => {
        e.stopPropagation();
        setIsLicenseModalOpen(true);
    };

    const openMoreInfoModal = (e, mensaje) => {
        e.stopPropagation();
        setInfoText(mensaje);
        setIsMoreInfoModalOpen(true);
    };

    const closeLicenseModal = () => setIsLicenseModalOpen(false);

    const closeMoreInfoModal = () => setIsMoreInfoModalOpen(false);

    useEffect(() => {
        addLocale('es', {
            firstDayOfWeek: 1,
            dayNames: [
              'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'
            ],
            dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
            dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
            monthNames: [
              'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
              'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
            ],
            monthNamesShort: [
              'ene', 'feb', 'mar', 'abr', 'may', 'jun',
              'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
            ],
            today: 'Hoy',
            clear: 'Limpiar'
        });

        addLocale('fr', {
            firstDayOfWeek: 1,
            dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
            dayNamesShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
            dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
            monthNames: [
              'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
              'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
            ],
            monthNamesShort: [
              'jan', 'fév', 'mar', 'avr', 'mai', 'jun',
              'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'
            ],
            today: "Aujourd'hui",
            clear: 'Effacer'
        });

        addLocale('it', {
            firstDayOfWeek: 1,
            dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
            dayNamesShort: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
            dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
            monthNames: [
              'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
              'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
            ],
            monthNamesShort: [
              'gen', 'feb', 'mar', 'apr', 'mag', 'giu',
              'lug', 'ago', 'set', 'ott', 'nov', 'dic'
            ],
            today: 'Oggi',
            clear: 'Cancella'
        });

        addLocale('pt', {
            firstDayOfWeek: 1,
            dayNames: [
              'domingo', 'segunda-feira', 'terça-feira',
              'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'
            ],
            dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
            dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
            monthNames: [
              'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
              'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
            ],
            monthNamesShort: [
              'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
              'jul', 'ago', 'set', 'out', 'nov', 'dez'
            ],
            today: 'Hoje',
            clear: 'Limpar'
        });

        addLocale('el', {
            firstDayOfWeek: 1,
            dayNames: ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
            dayNamesShort: ['Κυρ', 'Δευ', 'Τρί', 'Τετ', 'Πέμ', 'Παρ', 'Σάβ'],
            dayNamesMin: ['Κ', 'Δ', 'Τ', 'Τ', 'Π', 'Π', 'Σ'],
            monthNames: [
              'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος',
              'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος',
              'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
            ],
            monthNamesShort: ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαι', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'],
            today: 'Σήμερα',
            clear: 'Καθαρισμός'
        });

        const navLang = navigator.language || navigator.userLanguage;
        const langCode = navLang.slice(0, 2).toLowerCase();
        const supported = ['es', 'fr', 'it', 'pt', 'el'];
        if (supported.includes(langCode)) {
            locale(langCode);
        } else {
            locale('en');
        }

        const today = new Date();
        const tenDaysAgo = new Date();
        tenDaysAgo.setDate(today.getDate() - 10);
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        setMinDate(tenDaysAgo);
        setMaxDate(yesterday);
    }, []);

    useEffect(() => {
        if (!selectedDate) {
            setDiffDays(0);
            return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sel = new Date(selectedDate);
        sel.setHours(0, 0, 0, 0);
        const diffMs = today.getTime() - sel.getTime();
        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        const days = Math.round(diffMs / millisecondsPerDay);
        setDiffDays(days);
    }, [selectedDate]);

    useEffect(() => {
        const checkWrapped = () => {
            if (labelRef.current && linkRef.current) {
                const labelTop = labelRef.current.offsetTop;
                const linkTop = linkRef.current.offsetTop;
                setIsWrapped(linkTop > labelTop);
            }
        };
        checkWrapped();
        window.addEventListener('resize', checkWrapped);
        return () => window.removeEventListener('resize', checkWrapped);
    }, []);

    useEffect(() => {
        if (window.innerWidth < 447) {
            setIsWrapped(true);
        }
    }, []);

    /**
     * Invalida la dirección cacheada si cambian las coordenadas o desaparece la ubicación.
     * No dispara la petición: el reverse a Nominatim solo se hace al abrir el diálogo del mapa.
     */
    useEffect(() => {
        setMapAddress('');
    }, [mapLat, mapLon, hasMapLocation]);

    const handleMapLoad = () => {};

    const fetchMapAddress = async () => {
        if (!hasMapLocation) return;
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
                params: {
                    lat: mapLat,
                    lon: mapLon,
                    format: 'json',
                    'accept-language': navigator.language || 'en'
                }
            });
            setMapAddress(response.data.display_name || '');
        } catch (error) {
            console.error('Error fetching address:', error);
        }
    };

    const openMapDialog = () => {
        setIsMapDialogVisible(true);
        if (!mapAddress) {
            fetchMapAddress();
        }
    };

    const handleOpenMaps = () => {
        if (mapLat && mapLon) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLon}`, '_blank');
        }
    };

    // Maneja el evento de clic en el componente. Si hay algún modal abierto, no se navega.
    const openChartModal = (mode, code = null) => {
        if (isAnyModalOpen) return;
        if (!isAvailableLicense && !isFields) return;
        setChartModalMode(mode);
        setChartModalPathogenCode(code);
        setIsChartTimelineModalOpen(true);
    };

    const handleRowClick = (mode, code = null) => (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        openChartModal(mode, code);
    };

    const handleRowKeyDown = (mode, code = null) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openChartModal(mode, code);
        }
    };

    /** Icono junto al texto de estado: mismo color que el estado (columna izquierda del icono). */
    const renderFieldCardChartInfoIcon = (mode, pathogenCode = null, iconColor = 'var(--primary-color)') => (
        <button
            type="button"
            className="p-0 border-none bg-transparent cursor-pointer flex-shrink-0"
            style={{ lineHeight: 0, color: iconColor }}
            title={t('field_card.info')}
            aria-label={t('field_card.info')}
            onClick={(e) => {
                e.stopPropagation();
                openChartModal(mode, pathogenCode);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    openChartModal(mode, pathogenCode);
                }
            }}
        >
            <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '12px' }} />
        </button>
    );

    const handleClickBuyLicense = () => {
        navigate('/addLicense', { state: { sensorNumber } });
    };

    const handleClickLinkSensor = () => {
        navigate('/link/sensor', { state: { fieldId } });
    };

    const getSignalIcon = (signalSensor) => {
        switch (signalSensor) {
            case 'well_signal':
                return well_signal;
            case 'half_signal':
                return half_signal;
            case 'low_signal':
                return low_signal;
            default:
                return not_signal;
        }
    };

    const getBatteryIcon = (batteryStatus) => {
        switch (batteryStatus) {
            case 'not_signal_battery':
                return not_signal_battery;
            case 'no_battery':
                return not_battery;
            case 'low_battery':
                return low_battery;
            case 'half_battery':
                return half_battery;
            default:
                return well_battery;
        }
    };

    const openSharedUsersModal = async () => {
        try {
            const response = await getGuests(fieldId);
            setSharedUsers(response.results);
            setIsSharedUsersModalOpen(true);
        } catch (error) {
            console.error("Error al obtener los usuarios compartidos:", error);
        }
    };

    const closeSharedUsersModal = () => {
        setIsSharedUsersModalOpen(false);
    };

    const openRejectSharedModal = async () => {
        try {
            setIsRejectSharedModalOpen(true);
        } catch (error) {
            console.error("Error al obtener los usuarios compartidos:", error);
        }
    };

    const closeRejectSharedModal = () => {
        setIsRejectSharedModalOpen(false);
    };

    const openDeleteFieldModal = () => {
        setIsDeleteFieldModalOpen(true);
    };

    const closeDeleteFieldModal = () => {
        setIsDeleteFieldModalOpen(false);
    };

    const openUnlinkSensorModal = () => {
        setIsUnlinkSensorModalOpen(true);
    };

    const closeUnlinkSensorModal = () => {
        setIsUnlinkSensorModalOpen(false);
    };

    const handleStartModel = () => {
        if (menuModelType === 'No model' && isAvailableLicense !== true) {
            setOptionsErrorMessage(t('field_card.license_expired'));
            setTimeout(() => setOptionsErrorMessage(''), 5000);
            return;
        }
        setShowStartModelDialog(true);
    };

    const handleStopNematode = () => {
        setShowStopNematodeDialog(true);
    };

    const handleStopSolarization = () => {
        setShowStopSolarizationDialog(true);
    };

    const handleStartNematode = async () => {
        if (!fieldData) return;
        setOptionsLoading(true);
        const error = await postNematode(fieldData.id);
        if (!error) {
            if (fieldData.sensors.length > 0) {
                await interpolateData(diffDays, fieldData.sensors[0].id);
            }
            if (setTrigger) setTrigger((prev) => !prev);
            setOptionsLoading(false);
        } else {
            setOptionsErrorMessage(`Error al iniciar modelo: ${error}`);
            setTimeout(() => setOptionsErrorMessage(''), 5000);
            setOptionsLoading(false);
        }
    };

    const handleStartSolarization = async () => {
        if (!fieldData) return;
        setOptionsLoading(true);
        const error = await postSolarization(fieldData.id);
        if (!error) {
            if (fieldData.sensors.length > 0) {
                await interpolateData(diffDays, fieldData.sensors[0].id);
            }
            if (setTrigger) setTrigger((prev) => !prev);
            setOptionsLoading(false);
        } else {
            setOptionsErrorMessage(`Error al iniciar modelo: ${error}`);
            setTimeout(() => setOptionsErrorMessage(''), 5000);
            setOptionsLoading(false);
        }
    };

    /** Invitado: misma forma que el menú del propietario (submenú + primer ítem oculto) para que el foco inicial no marque «Desvincular». */
    const optionsMenuItems = showGuestOptionsMenu
        ? [
              {
                  label: '\u200B',
                  items: [
                      {
                          label: '',
                          style: { display: 'none' },
                          command: () => {}
                      },
                      ...(hasMapLocation
                          ? [
                                {
                                    label: t('field.map'),
                                    icon: 'pi pi-map',
                                    command: () => {
                                        openMapDialog();
                                    }
                                }
                            ]
                          : []),
                      {
                          label: t('field_card.unlink'),
                          icon: 'pi pi-user-minus',
                          command: () => {
                              openRejectSharedModal();
                          }
                      }
                  ]
              }
          ]
        : showOwnerOptionsMenu
          ? [
                {
                    label: t('field_card.sensor'),
                    items: [
                        {
                            label: '',
                            style: { display: 'none' },
                            command: () => {}
                        },
                        {
                            label: t('field_card.change_sensor'),
                            icon: 'pi pi-pencil',
                            command: () => {
                                navigate(`/linkSensor/${fieldId}`);
                            }
                        },
                        {
                            label: t('field_card.unlink_sensor'),
                            icon: 'pi pi-times',
                            command: () => {
                                openUnlinkSensorModal();
                            }
                        }
                    ]
                },
                {
                    label: t('field_card.field'),
                    items: [
                        ...(hasMapLocation
                            ? [
                                  {
                                      label: t('field.map'),
                                      icon: 'pi pi-map',
                                      command: () => {
                                          openMapDialog();
                                      }
                                  }
                              ]
                            : []),
                        {
                            label: `${t('field_card.link')} ${t('field_card.field').toLowerCase()}`,
                            icon: 'pi pi-share-alt',
                            command: () => {
                                openSharedUsersModal();
                            }
                        },
                        {
                            label: t('field_card.delete_field'),
                            icon: 'pi pi-trash',
                            command: () => {
                                openDeleteFieldModal();
                            }
                        }
                    ]
                },
                {
                    label: t('field_card.model'),
                    items: [
                        {
                            label:
                                menuModelType === 'N'
                                    ? t('field_card.deactivate_nematode_model')
                                    : menuModelType === 'S'
                                      ? t('field_card.deactivate_solarization_model')
                                      : t('field_card.activate_model'),
                            icon: menuModelType === 'N' || menuModelType === 'S' ? 'pi pi-times' : 'pi pi-play',
                            disabled: menuModelType === 'No model' && isAvailableLicense !== true,
                            command: () => {
                                if (menuModelType === 'N') {
                                    handleStopNematode();
                                } else if (menuModelType === 'S') {
                                    handleStopSolarization();
                                } else {
                                    handleStartModel();
                                }
                            }
                        }
                    ]
                }
            ]
          : [];

    const isMobile = window.innerWidth < 700;
    const isSmallMobile = window.innerWidth < 375;
    /** Pasos de rejilla barra/estado: default → intermedio → estrecho al reducir el ancho. */
    const barGridInnerWidth = typeof window !== 'undefined' ? window.innerWidth : BAR_GRID_DEFAULT_MIN_WIDTH;
    const barGridTier =
        barGridInnerWidth < BAR_GRID_TIGHT_MAX_WIDTH
            ? 'tight'
            : barGridInnerWidth < BAR_GRID_DEFAULT_MIN_WIDTH
              ? 'intermediate'
              : 'default';
    const solarBarGridPercents = (() => {
        switch (barGridTier) {
            case 'tight':
                return {
                    name: BAR_GRID_TIGHT_NAME_COL_PCT,
                    bar: BAR_GRID_TIGHT_BAR_MAX_PCT,
                    minPx: BAR_GRID_TIGHT_BAR_MIN_PX
                };
            case 'intermediate':
                return {
                    name: BAR_GRID_INTERMEDIATE_NAME_COL_PCT,
                    bar: BAR_GRID_INTERMEDIATE_BAR_MAX_PCT,
                    minPx: BAR_GRID_INTERMEDIATE_BAR_MIN_PX
                };
            default:
                return { name: 21, bar: 55, minPx: 40 };
        }
    })();
    const barGridTemplateColumns =
        barGridTier === 'default'
            ? NEMATODE_ROW_GRID.gridTemplateColumns
            : `minmax(0, ${solarBarGridPercents.name}%) minmax(${solarBarGridPercents.minPx}px, ${solarBarGridPercents.bar}%) minmax(0, 1fr)`;
    const nematodeRowGridStyle = {
        ...NEMATODE_ROW_GRID,
        gridTemplateColumns: barGridTemplateColumns
    };

    const buildNoLicenseModalContent = () => {
        const mobileStyle = isMobile ? { lineHeight: '1.15', fontSize: '15px' } : {};
        if (userEmailLogin === ownerEmail) {
            if (haveSensor) {
                return (
                    <>
                        {isAvailableLicense
                            ? t('field_card.license_valid_model_text_owner')
                            : t('field_card.license_caducated_text_owner')}
                        <Link
                            to={`/linkSensor/${fieldId}`}
                            className="text-primary"
                            onClick={(e) => e.stopPropagation()}
                            style={mobileStyle}
                        >
                            {t('field_card.link_sensor_2_click')}
                        </Link>
                        <br />
                        {t('field_card.support_contact')}
                    </>
                );
            }
            return (
                <>
                    {t('field_card.no_sensor_linked_owner')}{' '}
                    <Link
                        to={`/linkSensor/${fieldId}`}
                        className="text-primary"
                        onClick={(e) => e.stopPropagation()}
                        style={mobileStyle}
                    >
                        {t('field_card.link_sensor_1_click')}
                    </Link>
                    <br />
                    {t('field_card.support_contact')}
                </>
            );
        }
        if (haveSensor) {
            return isAvailableLicense
                ? t('field_card.license_valid_model_text_shared')
                : t('field_card.license_caducated_text_shared');
        }
        return t('field_card.no_sensor_linked_shared');
    };

    /** Nematodos sin licencia para ese modelo: etiqueta + barra vacía + icono info (abre el mismo modal que «más información»). */
    const renderNematodeNoLicenseRow = (typeKey) => {
        const labelText = t(`field_card.${typeKey}`);
        const noAccessRowLabel = haveSensor ? t('field_card.no_available') : t('field_card.no_sensor');
        const handleNoLicenseInfo = (e) => {
            e.preventDefault();
            e.stopPropagation();
            openMoreInfoModal(e, buildNoLicenseModalContent());
        };
        const onInfoKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleNoLicenseInfo(e);
            }
        };
        return (
            <div style={{ ...nematodeRowGridStyle, marginBottom: '1rem' }}>
                <span
                    title={labelText}
                    style={{
                        fontSize: '11px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: '#757575',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                >
                    {labelText}
                </span>
                <div
                    style={{
                        ...NEMATODE_BAR_TRACK,
                        minHeight: `${NEMATODE_BAR_HEIGHT_PX}px`,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    aria-hidden
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        minWidth: 0
                    }}
                >
                    <button
                        type="button"
                        className="p-0 border-none bg-transparent cursor-pointer"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '6px',
                            maxWidth: '100%',
                            minWidth: 0,
                            lineHeight: 1.2,
                            color: 'red'
                        }}
                        title={t('field_card.link_more_info')}
                        aria-label={`${noAccessRowLabel}. ${t('field_card.link_more_info')}`}
                        onClick={handleNoLicenseInfo}
                        onKeyDown={onInfoKeyDown}
                    >
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: 'red'
                            }}
                        >
                            {sentenceCaseUi(noAccessRowLabel)}
                        </span>
                        <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '12px', flexShrink: 0, color: 'red' }} />
                    </button>
                </div>
            </div>
        );
    };

    /** Solarización (nematodo o patógeno) sin acceso: rejilla como la fila con datos + barra vacía + icono info. */
    const renderSolarNoLicenseRow = (labelText, titleAttr = labelText) => {
        const SOLAR_NAME_COL_PCT = solarBarGridPercents.name;
        const SOLAR_BAR_COL_PCT = solarBarGridPercents.bar;
        const SOLAR_BAR_MIN_PX = solarBarGridPercents.minPx;
        const SOLAR_BAR_HEIGHT_PX = 16;
        const solarRowGrid = {
            display: 'grid',
            gridTemplateColumns: `minmax(0, ${SOLAR_NAME_COL_PCT}%) minmax(${SOLAR_BAR_MIN_PX}px, ${SOLAR_BAR_COL_PCT}%) minmax(0, 1fr)`,
            gap: '8px',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            marginBottom: '1rem'
        };
        const solarBarTrackStyle = {
            position: 'relative',
            width: '100%',
            minWidth: 0,
            backgroundColor: '#DEE2E6',
            borderRadius: '50px',
            overflow: 'hidden',
            boxSizing: 'border-box'
        };
        const handleNoLicenseInfo = (e) => {
            e.preventDefault();
            e.stopPropagation();
            openMoreInfoModal(e, buildNoLicenseModalContent());
        };
        const onInfoKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleNoLicenseInfo(e);
            }
        };
        const noAccessRowLabel = haveSensor ? t('field_card.no_available') : t('field_card.no_sensor');
        return (
            <div style={{ ...solarRowGrid }}>
                <span
                    title={titleAttr}
                    style={{
                        fontSize: '11px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: '#757575',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                >
                    {labelText}
                </span>
                <div
                    style={{
                        ...solarBarTrackStyle,
                        minHeight: `${SOLAR_BAR_HEIGHT_PX}px`,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    aria-hidden
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        minWidth: 0
                    }}
                >
                    <button
                        type="button"
                        className="p-0 border-none bg-transparent cursor-pointer"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '6px',
                            maxWidth: '100%',
                            minWidth: 0,
                            lineHeight: 1.2,
                            color: 'red'
                        }}
                        title={t('field_card.link_more_info')}
                        aria-label={`${noAccessRowLabel}. ${t('field_card.link_more_info')}`}
                        onClick={handleNoLicenseInfo}
                        onKeyDown={onInfoKeyDown}
                    >
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: 'red'
                            }}
                        >
                            {sentenceCaseUi(noAccessRowLabel)}
                        </span>
                        <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '12px', flexShrink: 0, color: 'red' }} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
        <ConfirmDialog />
        <Card 
            className={`mt-3`}
            style={{ 
                width: isSmallMobile ? '95.6666%' : '91.6666%',
                backgroundColor: 'white', 
                borderRadius: '10px',
                border: 'solid',
                borderRadius: '10px',
                borderWidth: '1px',
                borderColor: '#DEE2E6',
                boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.3)',
                maxWidth: fieldsPerRow === 0 || fieldsPerRow === 1 ? '100%' : fieldsPerRow === 2 ? '48%' : fieldsPerRow === 3 ? '32%' : '24%',
            }}
        >
            <div className={`pb-0`} style={{padding: isSmallMobile ? '0.5rem' : '0.75rem'}} >
                {optionsErrorMessage && (
                    <div className="p-2 mb-2 text-sm text-center border-round" style={{ backgroundColor: '#ffeeee', color: '#c00' }}>
                        {optionsErrorMessage}
                    </div>
                )}
                <div className="flex justify-content-between align-items-center">
                    <div className="flex align-items-center">
                        <div className="flex align-items-center">
                            <Badge 
                                className="flex align-items-center text-sm bg-white border-round-3xl pl-0 text-black border-2 border-solid font-normal"
                                style={{ borderColor: '#DEE2E6', lineHeight: '1.5', fontWeight: 'normal' }}
                                value={
                                    <>
                                        <span 
                                            style={{ 
                                                display: !haveSensor ? 'none' : 'block',
                                                width: '10px', 
                                                height: '10px', 
                                                borderRadius: '50%', 
                                                marginRight: '5px',
                                                marginLeft: '5px',
                                                backgroundColor: haveSensor && activeSensor === 'true' ? 'var(--primary-color)' : '#FF0000'
                                            }}
                                        ></span>
                                        <span style={{ marginLeft: !haveSensor ? '5px' : '0px', fontWeight: 'normal' }}>
                                            {haveSensor ? `${sensorNumber}` : sentenceCaseUi(sensorNumber)}
                                        </span>
                                    </>
                                }
                            />
                            {haveSensor ? (
                                <span 
                                    onClick={(e) => openLicenseModal(e)}
                                    style={{ cursor: 'pointer', color: isAvailableLicense ? isWarningLicense ? '#F6894E' : 'var(--primary-color)' : 'red', fontSize: '12px', marginLeft: '5px', userSelect: 'none' }}
                                >
                                    {isAvailableLicense ? isWarningLicense ? 
                                        <>
                                            <u>{t('field_card.license')}</u>{' '}
                                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ flexShrink: 0 }} />
                                        </> : 
                                        <>
                                            <u>{t('field_card.license')}</u> <FontAwesomeIcon icon={faCheck} /> 
                                        </> : 
                                        <>
                                            <u>{sentenceCaseUi(t('field_card.no_available'))}</u>{' '}
                                            <FontAwesomeIcon icon={faCircleInfo} style={{ color: 'red', fontSize: '12px' }} />
                                        </>
                                    }
                                </span>
                            ) : (
                                <span 
                                    onClick={(e) => openLicenseModal(e)}
                                    style={{ cursor: 'pointer', color: 'red', fontSize: '12px', marginLeft: '5px', userSelect: 'none', display: 'none' }}
                                >
                                    <>
                                        <u>{sentenceCaseUi(t('field_card.no_available'))}</u>{' '}
                                        <FontAwesomeIcon icon={faCircleInfo} style={{ color: 'red', fontSize: '12px' }} /> 
                                    </>
                                </span>
                            )}
                        </div>
                    </div>
                    {haveSensor && activeSensor === 'true' ? (
                        <div className="flex align-items-center text-600 text-sm">
                            <span className="flex align-items-center" style={{ marginLeft: '5px' }}>
                                {t(`field_card.${signalSensor}`)}
                                <SensorStatusIcon src={getSignalIcon(signalSensor)} />
                            </span>
                            <span className="flex align-items-center" style={{ marginLeft: '5px' }}>
                                {batterySensor.value === -1 ? 'no_battery' : batterySensor.value}%
                                <SensorStatusIcon src={getBatteryIcon(batterySensor.status)} />
                            </span>
                        </div>
                    ) : (
                        <div className="hidden">
                            <span className="flex align-items-center text-600 text-sm" style={{ marginLeft: '5px' }}>
                                {t(`field_card.${'no_signal'}`)}
                                <SensorStatusIcon src={getSignalIcon('no_signal')} />
                            </span>
                            <span className="flex align-items-center text-600 text-sm">
                                <SensorStatusIcon src={getBatteryIcon('not_signal_battery')} />
                            </span>
                        </div>
                    )}
                </div>

                <div style={{
                    borderBottom: '1px solid #DEE2E6',
                    margin: isSmallMobile ? '0 -0.5rem' : '0 -0.75rem',
                    paddingTop: isSmallMobile ? '0.5rem' : '0.75rem'
                }}>

                </div>
                <div className="flex align-items-flex-start justify-content-between gap-2" style={{ paddingTop: '4px' }}>
                    <h3
                        className="font-normal text-black mb-0 mt-0"
                        style={{
                            fontSize: isSmallMobile ? '1rem' : '1.1rem',
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-all',
                            flex: '1 1 auto',
                            minWidth: 0
                        }}
                    >
                        {fieldName}
                    </h3>
                    {showOptionsMenuHeader && (
                        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                            <Menu
                                className="mt-2"
                                model={optionsMenuItems}
                                popup
                                ref={optionsMenuRef}
                                id={`popup_menu_field_${fieldId}`}
                                transitionOptions={MENU_POPUP_TRANSITION_NO_INITIAL_FOCUS}
                                pt={{
                                    action: { style: { padding: '10px' } },
                                    label: { style: { color: 'black' } },
                                    icon: { style: { color: 'black' } },
                                    submenuheader: showGuestOptionsMenu
                                        ? {
                                              className: 'm-0 p-0 border-none',
                                              style: {
                                                  display: 'none',
                                                  height: 0,
                                                  overflow: 'hidden',
                                                  margin: 0,
                                                  padding: 0,
                                                  lineHeight: 0,
                                                  fontSize: 0
                                              }
                                          }
                                        : { style: { padding: '10px', color: 'black' } }
                                }}
                            />
                            <span
                                className="font-medium text-primary text-sm cursor-pointer"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    optionsMenuRef.current?.toggle(event);
                                }}
                                aria-controls={`popup_menu_field_${fieldId}`}
                                aria-haspopup
                                style={{ display: 'flex', marginTop: '4px' }}
                            >
                                {t('field.options')}
                                <FontAwesomeIcon className="pr-1" icon={faEllipsisV} style={{ marginLeft: '5px', height: '18px' }} />
                            </span>
                        </div>
                    )}
                </div>
                
                <div
                    className={`flex align-items-center flex-wrap gap-1 mt-0 ${model[0] === 'No model' ? 'mb-2' : 'mb-1'}`}
                >
                    <span className="text-600 text-sm">
                        {t(`field.crops.${cropId}`)}
                        {model[0] === 'Nematode active' && isAvailableLicense && (
                            <>
                                {model[1] === 0 && ` - ${t('field_card.generation').charAt(0).toUpperCase() + t('field_card.generation').slice(1)} ${t('field_card.initial')}`}
                                {model[1] !== 0 && ` - ${model[1]}º ${t('field_card.generation')}`}
                            </>
                        )}
                    </span>
                </div>

                {model[0] === 'Nematode active' &&
                    (() => {
                        const eggUi = getNematodeBarUi(model[3], model[2]);
                        const larvUi = getNematodeBarUi(model[6], model[5]);
                        return (
                    <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                        {isAvailableLicense && license_info.nematode_ovicide ? (
                            <div
                                role="button"
                                tabIndex={0}
                                style={{ ...nematodeRowGridStyle, cursor: 'pointer' }}
                                onClick={handleRowClick('ovicide')}
                                onKeyDown={handleRowKeyDown('ovicide')}>
                                <span
                                    className="text-black"
                                    title={t('field_card.egg')}
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        textAlign: 'left',
                                        color: '#000',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        ...(model[3] === 'red' ? { animation: 'blinker 1s linear infinite' } : {})
                                    }}
                                >
                                    {t('field_card.egg')}
                                </span>
                                <div style={NEMATODE_BAR_TRACK}>
                                    <ProgressBar
                                        value={eggUi.barValue}
                                        color={eggUi.barColor}
                                        showValue={false}
                                        style={{
                                            borderRadius: '20px',
                                            height: `${NEMATODE_BAR_HEIGHT_PX}px`,
                                            backgroundColor: 'transparent',
                                            width: '100%'
                                        }}
                                        pt={{
                                            value: { style: { backgroundColor: eggUi.barColor } }
                                        }}
                                    />
                                    <span style={NEMATODE_PCT_IN_BAR}>{`${eggUi.pctLabel} %`}</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: '4px',
                                        minWidth: 0,
                                        width: '100%'
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: '4px',
                                            flex: '1 1 auto',
                                            textAlign: 'right',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            color: eggUi.barColor,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            ...(model[3] === 'red' ? { animation: 'blinker 1s linear infinite' } : {})
                                        }}
                                    >
                                        {model[3] === 'blue'
                                            ? sentenceCaseUi(t('field_card.wait'))
                                            : model[3] === 'orange'
                                              ? sentenceCaseUi(t('field_card.prepare'))
                                              : model[3] === 'red'
                                                ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ flexShrink: 0 }} />
                                                        {sentenceCaseUi(t('field_card.treat'))}
                                                    </>
                                                  )
                                                : sentenceCaseUi(t('field_card.wait'))}
                                    </span>
                                    {renderFieldCardChartInfoIcon('ovicide', null, eggUi.barColor)}
                                </div>
                            </div>
                        ) : (
                            renderNematodeNoLicenseRow('egg')
                        )}
                        {isAvailableLicense && license_info.nematode_larvicide ? (
                            <div
                                role="button"
                                tabIndex={0}
                                style={{ ...nematodeRowGridStyle, cursor: 'pointer' }}
                                onClick={handleRowClick('larvicide')}
                                onKeyDown={handleRowKeyDown('larvicide')}>
                                <span
                                    className="text-black"
                                    title={t('field_card.larvicide')}
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        textAlign: 'left',
                                        color: '#000',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        ...(model[6] === 'red' ? { animation: 'blinker 1s linear infinite' } : {})
                                    }}
                                >
                                    {t('field_card.larvicide')}
                                </span>
                                <div style={NEMATODE_BAR_TRACK}>
                                    <ProgressBar
                                        value={larvUi.barValue}
                                        color={larvUi.barColor}
                                        showValue={false}
                                        style={{
                                            borderRadius: '20px',
                                            height: `${NEMATODE_BAR_HEIGHT_PX}px`,
                                            backgroundColor: 'transparent',
                                            width: '100%'
                                        }}
                                        pt={{
                                            value: { style: { backgroundColor: larvUi.barColor } }
                                        }}
                                    />
                                    <span style={NEMATODE_PCT_IN_BAR}>{`${larvUi.pctLabel} %`}</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: '4px',
                                        minWidth: 0,
                                        width: '100%'
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: '4px',
                                            flex: '1 1 auto',
                                            textAlign: 'right',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            color: larvUi.barColor,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            ...(model[6] === 'red' ? { animation: 'blinker 1s linear infinite' } : {})
                                        }}
                                    >
                                        {model[6] === 'blue'
                                            ? sentenceCaseUi(t('field_card.wait'))
                                            : model[6] === 'orange'
                                              ? sentenceCaseUi(t('field_card.prepare'))
                                              : model[6] === 'red'
                                                ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ flexShrink: 0 }} />
                                                        {sentenceCaseUi(t('field_card.treat'))}
                                                    </>
                                                  )
                                                : sentenceCaseUi(t('field_card.wait'))}
                                    </span>
                                    {renderFieldCardChartInfoIcon('larvicide', null, larvUi.barColor)}
                                </div>
                            </div>
                        ) : (
                            renderNematodeNoLicenseRow('larvicide')
                        )}
                    </div>
                        );
                    })()}

                {model[0] === 'Solarization active' && (
                    <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                        {(() => {
                            const nematodaLicensed = Boolean(isAvailableLicense && license_info?.solarization);
                            const pathogenCanShowBar = (pc) =>
                                Boolean(
                                    isAvailableLicense &&
                                    license_info?.solarization &&
                                    isPathogenSolarizationLicensed(license_info, pc)
                                );
                            const pathogensLatest = Array.isArray(model[3]) ? model[3] : [];
                            const nemTier = getSolarizationTierKey(model[1]);
                            const nemBar = getSolarizationTierBarColor(nemTier);
                            const nemDeg = String(Math.round(Number(model[1]) || 0));
                            const SOLAR_NAME_COL_PCT = solarBarGridPercents.name;
                            const SOLAR_BAR_COL_PCT = solarBarGridPercents.bar;
                            const SOLAR_BAR_MIN_PX = solarBarGridPercents.minPx;
                            const SOLAR_BAR_HEIGHT_PX = 16;
                            const solarRowGrid = {
                                display: 'grid',
                                gridTemplateColumns: `minmax(0, ${SOLAR_NAME_COL_PCT}%) minmax(${SOLAR_BAR_MIN_PX}px, ${SOLAR_BAR_COL_PCT}%) minmax(0, 1fr)`,
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',
                                minWidth: 0,
                                marginBottom: '1rem'
                            };
                            const solarBarTrackStyle = {
                                position: 'relative',
                                width: '100%',
                                minWidth: 0,
                                backgroundColor: '#DEE2E6',
                                borderRadius: '50px',
                                overflow: 'hidden',
                                boxSizing: 'border-box'
                            };
                            const solarDegInBarStyle = {
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: '11px',
                                fontWeight: '700',
                                color: '#212529',
                                pointerEvents: 'none',
                                zIndex: 1,
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                            };
                            return (
                                <>
                                    <div
                                        className="text-600"
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: '500',
                                            marginBottom: '0.6rem',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {t('field_card.solarization_nematodes_heading')}
                                    </div>
                                    {nematodaLicensed ? (
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            style={{ ...solarRowGrid, cursor: 'pointer' }}
                                            onClick={handleRowClick('nematode-solar')}
                                            onKeyDown={handleRowKeyDown('nematode-solar')}
                                        >
                                            <span
                                                className="text-black"
                                                title={t('field_card.solarization_nematodes_heading')}
                                                style={{
                                                    fontSize: '11px',
                                                    fontWeight: '500',
                                                    textAlign: 'left',
                                                    color: '#000',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0
                                                }}
                                            >
                                                {t('field_card.solarization_nematodes_heading')}
                                            </span>
                                            <div style={solarBarTrackStyle}>
                                                <ProgressBar
                                                    value={getSolarizationBarPercent(model[1])}
                                                    color={nemBar}
                                                    showValue={false}
                                                    style={{
                                                        borderRadius: '20px',
                                                        height: `${SOLAR_BAR_HEIGHT_PX}px`,
                                                        backgroundColor: 'transparent',
                                                        width: '100%'
                                                    }}
                                                    pt={{
                                                        value: { style: { backgroundColor: nemBar } }
                                                    }}
                                                />
                                                <span style={solarDegInBarStyle}>{`${nemDeg} °C`}</span>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    gap: '4px',
                                                    minWidth: 0,
                                                    width: '100%'
                                                }}
                                            >
                                                <span
                                                    title={sentenceCaseUi(t(`field_card.${nemTier}`))}
                                                    style={{
                                                        flex: '1 1 auto',
                                                        textAlign: 'right',
                                                        fontSize: '11px',
                                                        fontWeight: '600',
                                                        color: nemBar,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: 0
                                                    }}
                                                >
                                                    {sentenceCaseUi(t(`field_card.${nemTier}`))}
                                                </span>
                                                {renderFieldCardChartInfoIcon('nematode-solar', null, nemBar)}
                                            </div>
                                        </div>
                                    ) : (
                                        renderSolarNoLicenseRow(
                                            t('field_card.solarization_nematodes_heading'),
                                            t('field_card.solarization_nematodes_heading')
                                        )
                                    )}
                                    <div
                                        className="text-600"
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: '500',
                                            marginBottom: '0.6rem',
                                            marginTop: '1rem',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {t('field_card.solarization_soil_borne_heading')}
                                    </div>
                                    {PATHOGEN_SOLARIZATION_ORDER.map((pathogenCode) => {
                                        const codeUpper = String(pathogenCode).toUpperCase();
                                        if (!pathogenCanShowBar(codeUpper)) {
                                            const pLabel = getPathogenLatinLabel(codeUpper);
                                            return (
                                                <React.Fragment key={codeUpper}>
                                                    {renderSolarNoLicenseRow(pLabel, pLabel)}
                                                </React.Fragment>
                                            );
                                        }
                                        const rawRow =
                                            pathogensLatest.find(
                                                (p) => String(p?.pathogen_code).toUpperCase() === codeUpper
                                            ) || { pathogen_code: codeUpper, degrees: 0 };
                                        const pathogen = { ...rawRow, pathogen_code: codeUpper };
                                        const pDeg = Number(pathogen.degrees) || 0;
                                        const barPct = getPathogenSolarizationBarPercent(pDeg, pathogen.pathogen_code);
                                        const pDegLabel = String(Math.round(pDeg));
                                        const isPathogenSoilBorneComplete = Math.round(barPct) >= 100;
                                        const pathStatusShort = isPathogenSoilBorneComplete
                                            ? t('field_card.solar_pathogen_completed')
                                            : t('field_card.solar_pathogen_wait');
                                        const pathogenTierLabel = pathStatusShort;
                                        const pathBar = isPathogenSoilBorneComplete ? getColor('green') : getColor('orange');
                                        const pathogenName = getPathogenLatinLabel(pathogen.pathogen_code);
                                        return (
                                            <div
                                                key={pathogen.pathogen_code}
                                                role="button"
                                                tabIndex={0}
                                                style={{ ...solarRowGrid, cursor: 'pointer' }}
                                                onClick={handleRowClick('pathogen', pathogen.pathogen_code)}
                                                onKeyDown={handleRowKeyDown('pathogen', pathogen.pathogen_code)}
                                            >
                                                <span
                                                    className="text-black"
                                                    title={pathogenName}
                                                    style={{
                                                        fontSize: '11px',
                                                        fontWeight: '500',
                                                        textAlign: 'left',
                                                        color: '#000',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: 0
                                                    }}
                                                >
                                                    {pathogenName}
                                                </span>
                                                <div style={solarBarTrackStyle}>
                                                    <ProgressBar
                                                        value={barPct}
                                                        color={pathBar}
                                                        showValue={false}
                                                        style={{
                                                            borderRadius: '20px',
                                                            height: `${SOLAR_BAR_HEIGHT_PX}px`,
                                                            backgroundColor: 'transparent',
                                                            width: '100%'
                                                        }}
                                                        pt={{
                                                            value: { style: { backgroundColor: pathBar } }
                                                        }}
                                                    />
                                                    <span style={solarDegInBarStyle}>{`${pDegLabel} °C`}</span>
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        gap: '4px',
                                                        minWidth: 0,
                                                        width: '100%'
                                                    }}
                                                >
                                                    <span
                                                        title={sentenceCaseUi(pathogenTierLabel)}
                                                        style={{
                                                            flex: '1 1 auto',
                                                            textAlign: 'right',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            color: pathBar,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        {sentenceCaseUi(pathStatusShort)}
                                                    </span>
                                                    {renderFieldCardChartInfoIcon('pathogen', pathogen.pathogen_code, pathBar)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            );
                        })()}
                    </div>
                )}

            </div>
            <div 
                className="p-3 pt-2 pb-2"
                style={{ backgroundColor: "#DEE2E6", borderRadius: '0 0 10px 10px' }}
            >
                <span 
                    className="text-sm m-0 text-black" 
                    style={{ wordWrap: 'break-word', overflowWrap: 'break-word'}}
                >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <span 
                            style={{ 
                                wordBreak: 'break-all', 
                                whiteSpace: 'nowrap', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                paddingRight: '10px',
                                userSelect: 'none'
                            }}
                        >
                            <FontAwesomeIcon style={{ marginRight: '5px' }} icon={faShareNodes} />
                            {userEmailLogin === ownerEmail ? t('field_card.you') : ownerEmail}
                        </span>
                    </div>
                </span>
            </div>

            {fieldData && isOwner && (
                <>
                    <ConfirmDialog
                        className="w-11 desktop-max-width border-round-2xl"
                        visible={showStartModelDialog}
                        header={t('field_card.activate_model')}
                        acceptLabel={t('field.nematode_model_button')}
                        rejectLabel={t('field.solarization_model_button')}
                        acceptClassName="bg-primary"
                        rejectClassName="bg-primary"
                        defaultFocus="none"
                        focusOnShow={false}
                        onHide={() => {
                            setShowStartModelDialog(false);
                            setTimeout(() => setIsFirstLoad(false), 200);
                        }}
                        accept={async () => {
                            setOptionsErrorMessage('');
                            if (showDatePicker === true && selectedDate == null) {
                                setTimeout(() => {
                                    setOptionsErrorMessage(t('field.error_interpolation_starting_date'));
                                }, 100);
                                return;
                            }
                            await handleStartNematode();
                            setShowStartModelDialog(false);
                        }}
                        reject={async () => {
                            setOptionsErrorMessage('');
                            if (showDatePicker === true && selectedDate == null) {
                                setTimeout(() => {
                                    setOptionsErrorMessage(t('field.error_interpolation_starting_date'));
                                }, 100);
                                return;
                            }
                            await handleStartSolarization();
                            setShowStartModelDialog(false);
                        }}
                        pt={{
                            header: { className: 'border-round-top-2xl' },
                            footer: { className: 'border-round-bottom-2xl' }
                        }}
                        message={
                            <>
                                <p className="m-0 mb-2">
                                    {isFirstLoad
                                        ? t('field.start_model_init_message')
                                        : t('field.start_model_message')}
                                </p>
                                <div>
                                    <div className="flex align-items-center flex-wrap mb-3 mt-3">
                                        <Checkbox
                                            inputId={`startDateCheckbox_${fieldId}`}
                                            style={{ color: 'var(--primary-color)' }}
                                            checked={showDatePicker}
                                            onChange={(e) => {
                                                setShowDatePicker(e.checked);
                                                setSelectedDate(null);
                                                setDiffDays(0);
                                            }}
                                        />
                                        <label htmlFor={`startDateCheckbox_${fieldId}`} className="ml-2" ref={labelRef}>
                                            {t('field.active_interpolation')}
                                        </label>
                                        <a
                                            href="#!"
                                            ref={linkRef}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowInfo((prev) => !prev);
                                            }}
                                            className="ml-2 flex-shrink-0"
                                            style={{
                                                color: 'var(--primary-color)',
                                                textDecoration: 'underline',
                                                paddingLeft: isWrapped ? '17px' : '0',
                                                paddingTop: isWrapped ? '2px' : '0'
                                            }}
                                        >
                                            {t('field.what_is_interpolation')}
                                        </a>
                                    </div>
                                </div>
                                {showInfo && (
                                    <div className="p-2 border-round border-1 surface-100 mb-3">
                                        <p className="m-0 text-sm text-gray-700">
                                            {t('field.interpolation_description')}
                                        </p>
                                    </div>
                                )}
                                {showDatePicker && (
                                    <div className="mt-2 mb-0">
                                        <Calendar
                                            id={`calendar_${fieldId}`}
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            placeholder={t('field.interpolation_starting_date')}
                                            readOnlyInput
                                            className="w-full"
                                            minDate={minDate}
                                            maxDate={maxDate}
                                        />
                                    </div>
                                )}
                            </>
                        }
                    />

                    <ConfirmDialog
                        className="w-11 desktop-max-width border-round-2xl"
                        visible={showStopNematodeDialog}
                        message={t('field.stop_nematode_message')}
                        header={t('field_card.deactivate_model')}
                        icon="pi pi-exclamation-triangle"
                        acceptLabel={t('field.confirmDialog')}
                        rejectLabel={t('field.rejectDialog')}
                        acceptClassName="bg-primary"
                        rejectClassName="bg-primary"
                        defaultFocus="none"
                        focusOnShow={false}
                        onHide={() => setShowStopNematodeDialog(false)}
                        accept={async () => {
                            if (!fieldData?.active_prediction_model) {
                                setShowStopNematodeDialog(false);
                                return;
                            }
                            setOptionsLoading(true);
                            const error = await putNematode(fieldData.id, fieldData.active_prediction_model.id);
                            if (!error) {
                                if (setTrigger) setTrigger((prev) => !prev);
                                setShowStopNematodeDialog(false);
                                setOptionsLoading(false);
                            } else {
                                setOptionsErrorMessage(`Error al detener modelo: ${error}`);
                                setTimeout(() => setOptionsErrorMessage(''), 5000);
                                setOptionsLoading(false);
                            }
                        }}
                        pt={{
                            header: { className: 'border-round-top-2xl' },
                            footer: { className: 'border-round-bottom-2xl' }
                        }}
                    />

                    <ConfirmDialog
                        className="w-11 desktop-max-width border-round-2xl"
                        visible={showStopSolarizationDialog}
                        message={t('field.stop_solarization_message')}
                        header={t('field_card.deactivate_model')}
                        icon="pi pi-exclamation-triangle"
                        acceptLabel={t('field.confirmDialog')}
                        rejectLabel={t('field.rejectDialog')}
                        acceptClassName="bg-primary"
                        rejectClassName="bg-primary"
                        defaultFocus="none"
                        focusOnShow={false}
                        onHide={() => setShowStopSolarizationDialog(false)}
                        accept={async () => {
                            if (!fieldData?.active_solarization) {
                                setShowStopSolarizationDialog(false);
                                return;
                            }
                            setOptionsLoading(true);
                            const error = await putSolarization(fieldData.id, fieldData.active_solarization.id);
                            if (!error) {
                                if (setTrigger) setTrigger((prev) => !prev);
                                setShowStopSolarizationDialog(false);
                                setOptionsLoading(false);
                            } else {
                                setOptionsErrorMessage(`Error al detener modelo: ${error}`);
                                setTimeout(() => setOptionsErrorMessage(''), 5000);
                                setOptionsLoading(false);
                            }
                        }}
                        pt={{
                            header: { className: 'border-round-top-2xl' },
                            footer: { className: 'border-round-bottom-2xl' }
                        }}
                    />

                    <ConfirmDialog
                        className="w-11 desktop-max-width border-round-2xl"
                        visible={isUnlinkSensorModalOpen}
                        onHide={closeUnlinkSensorModal}
                        message={t('field.unlink_sensor_message')}
                        header={t('field_card.unlink_sensor')}
                        acceptLabel={t('field_card.unlink')}
                        rejectLabel={t('settings.cancel')}
                        acceptClassName="bg-primary"
                        rejectClassName="p-confirm-dialog-reject p-button-text p-button p-component"
                        defaultFocus="none"
                        focusOnShow={false}
                        accept={async () => {
                            if (fieldData && fieldData.sensors[0] && fieldData.sensors[0].tst_serial_number) {
                                await unlinkSensor(fieldData.sensors[0].tst_serial_number);
                                closeUnlinkSensorModal();
                                if (setTrigger) setTrigger((prev) => !prev);
                            } else {
                                closeUnlinkSensorModal();
                            }
                        }}
                        pt={{
                            header: { className: 'border-round-top-2xl' },
                            footer: { className: 'border-round-bottom-2xl' }
                        }}
                    />
                </>
            )}

            <ModalShared 
                isOpen={isSharedUsersModalOpen} 
                onClose={closeSharedUsersModal} 
                sharedUsers={sharedUsers}
                fieldId={fieldId}
                setSharedUsers={setSharedUsers}
            />

            <ModalRejectShared 
                isOpen={isRejectSharedModalOpen} 
                onClose={closeRejectSharedModal}
                fieldId={fieldId}
                userId={userId}
                setTrigger={setTrigger}
            />

            <ModalDeleteField
                isOpen={isDeleteFieldModalOpen}
                onClose={closeDeleteFieldModal}
                fieldId={fieldId}
                userId={userId}
                setTrigger={setTrigger}
            />

            <ModalLicenseExpiration 
                isOpen={isLicenseModalOpen}
                onClose={closeLicenseModal}
                fieldId={fieldId}
                isOwner={isOwner}
                licenseInfo={license_info}
            />

            <ModalMoreInfo 
                isOpen={isMoreInfoModalOpen}
                onClose={closeMoreInfoModal}
                info_text={info_text}
            />

            {hasMapLocation && (
                <Dialog
                    header={<span className="text-color-secondary">{t('field.location')}</span>}
                    footer={
                        <div className="flex flex-column align-items-center">
                            <div className="flex flex-column" style={{ paddingTop: '1.5rem' }}>
                                <span className="text-color-secondary">
                                    {t('field.latitude')}: {mapLat.toFixed(3)} | {t('field.longitude')}: {mapLon.toFixed(3)}
                                </span>
                                <span className="text-color-secondary pt-1">
                                    {t('field.address')}: {mapAddress}
                                </span>
                            </div>
                            <span
                                className="underline text-color-secondary pt-1 cursor-pointer"
                                onClick={handleOpenMaps}
                            >
                                {t('field.open_maps')}
                            </span>
                        </div>
                    }
                    visible={isMapDialogVisible}
                    onHide={() => setIsMapDialogVisible(false)}
                    className="w-11 p-0 border-round-2xl"
                    focusOnShow={false}
                    pt={{
                        header: { className: 'border-round-top-2xl' },
                        content: { className: 'p-0' },
                        footer: { className: 'border-round-bottom-2xl text-center' }
                    }}
                >
                    <div
                        className="flex align-items-center justify-content-center w-full map-div"
                        style={{
                            height: FIELD_MAP_DIALOG_HEIGHT_PX,
                            minHeight: FIELD_MAP_DIALOG_HEIGHT_PX,
                            maxHeight: FIELD_MAP_DIALOG_HEIGHT_PX,
                            width: '100%',
                            overflow: 'hidden'
                        }}
                    >
                        <FieldMap
                            latitude={mapLat}
                            longitude={mapLon}
                            onMapLoad={handleMapLoad}
                            clickable={false}
                            height={FIELD_MAP_DIALOG_HEIGHT_PX}
                        />
                    </div>
                </Dialog>
            )}
        </Card>

        <FieldChartTimelineModal
            visible={isChartTimelineModalOpen}
            onHide={() => setIsChartTimelineModalOpen(false)}
            fieldId={fieldId}
            initialFieldData={fieldData}
            mode={chartModalMode}
            pathogenCode={chartModalPathogenCode}
        />
        </>
    );
};